import { useEffect, useLayoutEffect, useState } from 'react';
import type { Card, DiffOp, TextCheck } from '../lib/api';
import { markInferred } from '../lib/inferred';
import { rulesLine, versionLine, type EditorState, type Fields } from '../lib/editor';
import { whenLocal } from '../lib/time';

/**
 * The draft is the editor (fix-73, docs/design/card-mockup.html in the PSA repo): always editable in place, with no
 * mode. Inferred sentences are highlighted in the text being edited; the header shows the versions, the share of the
 * AI's first draft changed, and a diff against it; Refine is a box under the draft that rewrites it in place as the
 * next version; the rules line under it says, as he types, whether the text passes or which rule it would fail.
 */
export function DraftWorkspace({
  card, ed, pending, check, checkedFor, onType, onRefine, onDiscard, editorRef, focus, onFocusToggle, onContext, onEditorFocus,
}: {
  card: Card;
  ed: EditorState;
  pending: boolean;
  check: TextCheck | null;
  checkedFor: string | null;
  onType: (f: Partial<Fields>) => void;
  onRefine: (instruction: string) => Promise<boolean>;
  onDiscard: () => void;
  editorRef: React.RefObject<HTMLTextAreaElement | null>;
  /** fix-74: focus on or off (F), or null where the screen has no rails to hide; Context opens the slide-over. */
  focus: boolean | null;
  onFocusToggle: () => void;
  onContext: () => void;
  onEditorFocus: () => void;
}) {
  const [showDiff, setShowDiff] = useState(false);
  const [instruction, setInstruction] = useState('');
  const busy = ed.phase !== 'idle';
  const text = ed.draft.body;
  const unsaved = !!ed.base && (ed.base.body !== ed.draft.body || ed.base.subject !== ed.draft.subject);
  const ws = card.workspace;
  const fresh = check && checkedFor === text;
  const share = fresh ? check.share_changed : ws?.share_changed ?? 0;
  const line = rulesLine(check, checkedFor, text);
  const inferred = card.payload.inferred ?? [];
  const marked = markInferred(text, inferred);
  const anyMarked = marked.some((r) => r.inferred);

  // The textarea grows with its text, so the highlight layer under it never needs to scroll with it.
  useLayoutEffect(() => {
    const el = editorRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [text, showDiff, editorRef]);
  useEffect(() => { setShowDiff(false); setInstruction(''); }, [card.id]);

  const refine = async () => {
    if (!instruction.trim() || busy) return;
    if (await onRefine(instruction.trim())) setInstruction('');
  };

  return (
    <>
      <section className="panel ws-editor">
        <div className="ws-head mono">
          <span className="ws-version">DRAFT · v{ws?.versions?.length ?? 1}</span>
          <span className="ws-history">{versionLine(ws?.versions, (at) => whenLocal(at).replace(/^\w+ /, ''), unsaved)}</span>
          <span className="ws-share" title="Of the AI's first draft: 0 untouched, 100 replaced">share changed: {share}%</span>
          <button type="button" className={`ws-toggle ${showDiff ? 'is-on' : ''}`} onClick={() => setShowDiff((x) => !x)}
            aria-pressed={showDiff}>{showDiff ? 'hide diff' : 'show diff'}</button>
          {focus && <button type="button" className="ws-toggle" onClick={onContext}>context</button>}
          {focus !== null && (
            <button type="button" className={`ws-toggle ws-focus ${focus ? 'is-on' : ''}`} onClick={onFocusToggle}
              aria-pressed={focus} title="Focus: the draft full width (F)">{focus ? 'exit focus · F' : 'focus · F'}</button>
          )}
        </div>
        <div className="ws-subject">
          <label className="mono" htmlFor="draft-subject">Subject</label>
          <input id="draft-subject" value={ed.draft.subject} onChange={(e) => onType({ subject: e.target.value })}
            disabled={!pending} readOnly={busy} placeholder="(no subject)" />
        </div>
        {showDiff ? (
          <div className="ws-diff" aria-label="Changes against the AI draft">
            {fresh ? <Diff ops={check.diff} /> : <p className="mono ws-wait">working out the diff…</p>}
          </div>
        ) : (
          <div className="ws-body">
            <div className="ws-backdrop" aria-hidden="true">
              {marked.map((r, i) => (r.inferred ? <mark key={i}>{r.text}</mark> : <span key={i}>{r.text}</span>))}
              {'\n'}
            </div>
            <label className="sr-only" htmlFor="draft-body">Body</label>
            <textarea id="draft-body" ref={editorRef} className="ws-text" value={text} spellCheck
              onChange={(e) => onType({ body: e.target.value })} onFocus={onEditorFocus} disabled={!pending} readOnly={busy} />
          </div>
        )}
        {(anyMarked || inferred.length > 0) && (
          <p className="mono ws-legend">▲ highlighted = inferred by the drafter, not from a page · a draft with an
            inferred sentence is always held for you{!anyMarked ? ' · none of them is in the text now' : ''}</p>
        )}
        {pending && (
          <form className="ws-refine" onSubmit={(e) => { e.preventDefault(); void refine(); }}>
            <label className="sr-only" htmlFor="refine-box">Refine</label>
            <input id="refine-box" value={instruction} onChange={(e) => setInstruction(e.target.value)} disabled={busy}
              placeholder="Refine: tell the drafter what to change, e.g. 'shorter, drop the Auth ID sentence'" />
            <button type="submit" className="btn-secondary" disabled={busy || !instruction.trim()}>
              {ed.phase === 'refining' ? 'Rewriting…' : 'Refine'}
            </button>
          </form>
        )}
      </section>

      {pending && (
        <div className={`ws-rules ws-rules-${line.tone}`} role="status">
          <span className="ws-rules-mark">{line.tone === 'ok' ? '✓' : line.tone === 'bad' ? '✕' : line.tone === 'warn' ? '▲' : '…'}</span>
          <span>{line.text}</span>
          <span className="mono ws-rules-note">unsaved edits are kept if you leave this card</span>
        </div>
      )}
      {ed.newer && (
        <p className="ws-newer">A newer version of this draft was saved while you were editing. Your text is still here;
          Save my version replaces it.{' '}
          <button type="button" className="btn-ghost btn-small" onClick={onDiscard}>Discard my edits and load it</button></p>
      )}
    </>
  );
}

function Diff({ ops }: { ops: DiffOp[] }) {
  return (
    <p className="ws-diff-text">
      {ops.map(([op, t], i) => {
        const sep = i > 0 && !t.startsWith('\n') && !ops[i - 1][1].endsWith('\n') ? ' ' : '';
        if (op === '=') return <span key={i}>{sep}{t}</span>;
        return op === '-' ? <span key={i}>{sep}<del>{t}</del></span> : <span key={i}>{sep}<ins>{t}</ins></span>;
      })}
    </p>
  );
}
